import GAIAProductListProductPrice from "@gaiaherbs/Components/GAIAProductListProductPrice";
import ProductListActions, {
    ProductListActionsStyles,
} from "@gaiaherbs/Overrides/Widgets/ProductList/ProductListActions";
import ProductListProductImage from "@gaiaherbs/Widgets/HCP/ProductList/ProductListProductImage";
import ProductListProductInformation, {
    ProductListProductInformationStyles,
} from "@gaiaherbs/Widgets/HCP/ProductList/ProductListProductInformation";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import translate from "@insite/client-framework/Translate";
import ProductSearchScoreData from "@insite/content-library/Components/ProductSearchScoreData";
import { ProductCardSelections } from "@insite/content-library/Widgets/ProductList/ProductCardSelections";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps, GridItemStyle } from "@insite/mobius/GridItem";
import { IconWrapper } from "@insite/mobius/Icon";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import React, { FC } from "react";
import { css } from "styled-components";

type Props = ProductCardSelections & HasProduct;

export interface ProductListProductCardStyles {
    gridContainer?: GridContainerProps;
    leftColumnGridItem?: GridItemProps;
    rightColumnGridItem?: GridItemProps;
    rightColumnGridItemFullWidth?: GridItemProps;
    rightColumnGridContainer?: GridContainerProps;
    infoGridItem?: GridItemProps;
    priceGridItem?: GridItemProps;
    actionsGridItem?: GridItemProps;
    searchScoreDataGridItem?: GridItemProps;
    sponsoredText?: TypographyPresentationProps;
    actionStyles?: ProductListActionsStyles;
    productInfoStyles?: ProductListProductInformationStyles;
}

export const productCardStyles: ProductListProductCardStyles = {
    gridContainer: {
        gap: 0,
        css: css`
            padding: 0;
            min-width: 320px; /* prevent overlap with second column */
        `,
        offsetCss: css`
            max-width: 100%;
            margin: 0;
            padding: 0;
        `,
    },
    leftColumnGridItem: {
        width: [4, 2, 2, 3, 2],
        css: css`
            padding-right: 20px;
            flex-direction: column;
        `,
    },
    rightColumnGridItem: {
        width: [8, 10, 10, 9, 10],
    },
    rightColumnGridItemFullWidth: {
        width: 12,
    },
    rightColumnGridContainer: {
        offsetCss: css`
            max-width: 100%;
            margin: 0;
            padding: 0;
        `,
    },
    infoGridItem: {
        width: 12,
        // width: 6,
        css: css`
            padding: 0px;
        `,
    },
    priceGridItem: {
        // width: [12, 12, 3, 3, 3],
        width: 3,
        css: css`
            padding: 0px;
            align-items: center;
        `,
    },
    actionsGridItem: {
        // width: [12, 12, 3, 3, 3],
        width: 3,
        css: css`
            padding: 0px;
            align-items: center;
        `,
    },
    searchScoreDataGridItem: {
        width: 12,
    },
    sponsoredText: {
        css: css`
            margin-bottom: 5px;
            font-size: 0.9em;
            opacity: 0.5;
        `,
    },
    actionStyles: {
        price: {
            priceWrapper: {
                css: css`
                    padding: 0px;
                    display: flex;
                    align-items: center;
                    justify-content: flex-end;
                `,
            },
            savingsMessageText: {
                css: css`
                    align-self: flex-end;
                `,
            },
        },
        addToListWrapper: {
            css: css`
                margin-top: 4px;
                /* min-height: 2rem; */
                display: flex;
                align-items: center;
                justify-content: flex-end;
            `,
        },
        addToCartWrapper: {
            css: css`
                display: flex;
                align-items: center;
                margin-top: 0px;
                padding: 0px 0px 0px 15px;
            `,
        },
        addToCartButton: {
            css: css`
                width: 82%;
                min-height: 34px;
                font-size: 13px;
                font-family: "LatoBold";
                padding: 0px 2px;
                > span {
                    font-size: 13px;
                    font-family: "LatoBold";
                }
            `,
        },
        addToListLink: {
            link: {
                css: css`
                    font-size: 14px;
                    font-family: "LatoBold";
                `,
            },
        },
        quantityBreakPricing: {
            viewLink: {
                css: css`
                    align-self: flex-end;
                `,
            },
        },
    },
    productInfoStyles: {
        descriptionGridItem: {
            css: css`
                padding-top: 0px;
                padding-left: 0px;
                padding-bottom: 0px;
            `,
        },
        descriptionStyles: {
            productDetailLink: {
                color: "primary",
                css: css`
                    width: 100%;
                    font-size: 22px;
                    font-family: "LibreBaskerville";
                `,
                typographyProps: {
                    size: 22,
                    fontFamily: "LibreBaskerville",
                },
            },
            productDetailDescription: {
                color: "primary",
                size: 18,
                fontFamily: "LibreBaskerville",
            },
        },

        availabilityGridItem: {
            css: css`
                padding-top: 0px;
                padding-left: 0px;
                div[class^="InventoryMessage"] {
                    padding: 2px 4px;
                    span[class^="IconWrapper"] {
                        height: 20px;
                        width: 20px;
                    }
                }
            `,
        },
        availabilityStyles: {
            messageText: {
                size: 14,
            },
        },
        partNumbersStyles: {
            erpNumberLabelText: {
                css: css`
                    margin-top: 0px;
                    font-size: 14px;
                    font-family: "LatoBold";
                `,
            },
            erpNumberValueText: {
                css: css`
                    font-size: 14px;
                    font-family: "Lato";
                `,
            },
            container: {
                gap: 5,
                offsetCss: css`
                    width: 100% !important;
                    display: flex;
                    flex-direction: row;
                    > ${GridItemStyle} {
                        flex: 1 !important;
                    }
                `,
            },
        },
        partNumbersGridItem: {
            css: css`
                padding-top: 0px;
                padding-bottom: 5px;
                padding-left: 0px;
            `,
        },
    },
};

const styles = productCardStyles;

const ProductListProductCard: FC<Props> = props => {
    const rightColumnGridItemStyle = props.showImage ? styles.rightColumnGridItem : styles.rightColumnGridItemFullWidth;
    return (
        <GridContainer {...styles.gridContainer} data-test-selector={`productListProductCard${props.product.id}`}>
            {props.showImage && (
                <GridItem {...styles.leftColumnGridItem}>
                    {props.product.isSponsored && (
                        <Typography {...styles.sponsoredText}>{translate("Sponsored")}</Typography>
                    )}
                    <ProductListProductImage showImage={props.showImage} showCompare={props.showCompare} />
                </GridItem>
            )}
            <GridItem {...rightColumnGridItemStyle}>
                <GridContainer {...styles.rightColumnGridContainer}>
                    <GridItem {...styles.infoGridItem}>
                        <ProductListProductInformation
                            showBrand={props.showBrand}
                            showTitle={props.showTitle}
                            showPartNumbers={props.showPartNumbers}
                            showAvailability={props.showAvailability}
                            showAttributes={props.showAttributes}
                            extendedStyles={styles.productInfoStyles}
                        />
                    </GridItem>
                    {/* <GridItem {...styles.priceGridItem}>
                        <GAIAProductListProductPrice showPrice={props.showPrice} />
                    </GridItem> */}
                    {/* <GridItem {...styles.actionsGridItem}>
                        <ProductListActions
                            showPrice={false}
                            showAddToList={props.showAddToList}
                            extendedStyles={styles.actionStyles}
                        />
                    </GridItem> */}
                    {/* <GridItem {...styles.searchScoreDataGridItem}>
                        <ProductSearchScoreData />
                    </GridItem> */}
                </GridContainer>
            </GridItem>
        </GridContainer>
    );
};

export default withProduct(ProductListProductCard);
